import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  CellClickedEvent,
  ColumnState,
  ColDef,
  GridApi,
  ICellRendererParams,
} from "ag-grid-community";
import { useHistory } from "react-router-dom";
import { Alert } from "@mui/material";
import { RequisitionRow } from "../../recoil/types";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { getColDefs } from "./getColDefs";
import { NoRowsRenderer } from "./NoRowsRenderer";
import { LoadingRowsRenderer } from "./LoadingRowsRenderer";
import {
  AGGridWrapper,
  GridBody,
  GridWithPagination,
  StyledPhlebotomySnackbar,
} from "./Grid.styles";
import { DEFAULT_GRID_PAGE_SIZE, GridPagination } from "./GridPagination";
import {
  ReleasePatientData,
  AddOnModalData,
  SubTabName,
  TabName,
} from "./types";
import { REQUISITION_URL } from "../../utils/urlConstants";
import { NewRequisitionType } from "../NewRequisition/types";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import { NewReqOrderDialog } from "../NewRequisition/NewReqOrderDialog";
import { CfcRowBanner } from "../Cfc/CfcRowBanner";
import {
  CfcOrderDetailsDialog,
  isCfcOrder,
} from "../Cfc/CfcOrderDetailsDialog";
import {
  CFC_BANNER_HEIGHT,
  isEligibleForCfc,
  PROVIDER_GRID_ROW_HEIGHT,
} from "../Cfc/cfcGridUtils";
import { GRID_CLICK_SUPPRESSED_SELECTOR } from "./GridClickBoundary";

type CfcBannerRow = { kind: "cfcBanner"; row: RequisitionRow };
type GridRowInput = RequisitionRow | NewRequisitionType;
type GridRowData = GridRowInput | CfcBannerRow;

type CfcBannerContext = {
  onOrderCfc: (row: RequisitionRow) => void;
  currentPage: number;
};

type GridSortDirection = "asc" | "desc";
type GridSort = {
  colId: string;
  sort: GridSortDirection;
  sortIndex?: number | null;
};

const RESULT_CELL_COLUMN_IDS = new Set([
  "carrier",
  "aneuploidy",
  "patientStatus",
  "rhd",
  "fetalSex",
]);

export const isCfcBannerRow = (data?: GridRowData): data is CfcBannerRow =>
  Boolean(data && (data as CfcBannerRow).kind === "cfcBanner");

const isProviderGridRow = (data?: GridRowData): data is RequisitionRow =>
  Boolean(data && !isCfcBannerRow(data) && "reqID" in data);

const isOrderedGridRow = (data?: GridRowData): data is NewRequisitionType =>
  Boolean(
    data &&
    !isCfcBannerRow(data) &&
    "unity_complete_id" in data &&
    !("reqID" in data),
  );

const getGridClassName = (physicalDeviceType: string): string =>
  `ag-theme-alpine ${
    physicalDeviceType === "mobile"
      ? "cfc-no-pinned-cols"
      : "cfc-has-pinned-cols"
  }`;

const getGridRowId = (rowData?: GridRowData): string => {
  if (isCfcBannerRow(rowData)) {
    return `cfc-banner-${rowData.row.reqID}`;
  }
  if (isProviderGridRow(rowData)) {
    return rowData.sfID || rowData.reqID;
  }
  if (isOrderedGridRow(rowData)) {
    return rowData.unity_complete_id;
  }
  return "";
};

const shouldOpenRequisitionFromColumn = (columnId: string): boolean =>
  !RESULT_CELL_COLUMN_IDS.has(columnId);

const SAVED_PAGE_KEY_PREFIX = "btoProviderCurrentPage";

const getClickedCellElement = (event?: Event | null): Element | null => {
  const target = event?.target;
  if (target instanceof Element) {
    return target.closest(".ag-cell") ?? target;
  }
  if (target instanceof Node) {
    return target.parentElement?.closest(".ag-cell") ?? target.parentElement;
  }
  return null;
};

const hasSelectedTextInClickedCell = (event?: Event | null): boolean => {
  const selection = window.getSelection();
  if (!selection?.toString().length || !selection.rangeCount) return false;

  const clickedCell = getClickedCellElement(event);
  if (!clickedCell) return false;

  for (let index = 0; index < selection.rangeCount; index += 1) {
    if (selection.getRangeAt(index).intersectsNode(clickedCell)) {
      return true;
    }
  }
  return false;
};

const hasSuppressedGridClickTarget = (event?: Event | null): boolean => {
  const target = event?.target;
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest(GRID_CLICK_SUPPRESSED_SELECTOR));
};

const CfcBannerFullWidthRenderer = (
  params: ICellRendererParams<GridRowData> & { context: CfcBannerContext },
) => {
  if (!isCfcBannerRow(params.data)) return null;
  return (
    <CfcRowBanner
      row={params.data.row}
      onOrderCfc={params.context.onOrderCfc}
    />
  );
};

export const buildDisplayRowsWithCfcBanners = (
  rows: GridRowInput[] | undefined,
  shouldShowCfcBanners: boolean,
): GridRowData[] | undefined => {
  if (!rows) return rows;
  if (!shouldShowCfcBanners) return rows;

  const result: GridRowData[] = [];
  for (const row of rows) {
    if (
      isProviderGridRow(row) &&
      isEligibleForCfc(row, { requireConditions: true })
    ) {
      result.push({ kind: "cfcBanner", row });
    }
    result.push(row);
  }
  return result;
};

export const getPaginatedRows = <T,>(
  rows: T[] | undefined,
  currentPage: number,
  pageSize: number,
): T[] | undefined => {
  if (!rows) return rows;
  const pageStartIndex = currentPage * pageSize;
  return rows.slice(pageStartIndex, pageStartIndex + pageSize);
};

const getColumnId = (colDef: ColDef): string | undefined =>
  colDef.colId ?? (typeof colDef.field === "string" ? colDef.field : undefined);

const isGridSortDirection = (
  sort: ColumnState["sort"] | ColDef["sort"] | ColDef["initialSort"],
): sort is GridSortDirection => sort === "asc" || sort === "desc";

const bySortIndex = (first: GridSort, second: GridSort) =>
  (first.sortIndex ?? Number.MAX_SAFE_INTEGER) -
  (second.sortIndex ?? Number.MAX_SAFE_INTEGER);

const isGridSort = (sort: GridSort | undefined): sort is GridSort =>
  Boolean(sort);

const getDefaultSortModel = (columnDefs: ColDef[]): GridSort[] =>
  columnDefs
    .map((colDef, index) => {
      const colId = getColumnId(colDef);
      const sortDirection = colDef.initialSort ?? colDef.sort;
      if (!colId || !isGridSortDirection(sortDirection)) return undefined;
      const sort: GridSort = {
        colId,
        sort: sortDirection,
        sortIndex: colDef.initialSortIndex ?? colDef.sortIndex ?? index,
      };
      return sort;
    })
    .filter(isGridSort)
    .sort(bySortIndex);

const getSortModelFromColumnState = (columnState: ColumnState[]): GridSort[] =>
  columnState
    .map(({ colId, sort, sortIndex }) => {
      if (!colId || !isGridSortDirection(sort)) return undefined;
      const gridSort: GridSort = { colId, sort, sortIndex };
      return gridSort;
    })
    .filter(isGridSort)
    .sort(bySortIndex);

const getSavedColumnState = (tabType: TabName): ColumnState[] | undefined => {
  const storageKey = `btoProviderColumnState${tabType}`;
  const serializedState = localStorage?.getItem(storageKey);
  if (!serializedState) return undefined;

  try {
    const columnState: unknown = JSON.parse(serializedState);
    if (
      Array.isArray(columnState) &&
      columnState.every(
        (column) =>
          typeof column === "object" &&
          column !== null &&
          "colId" in column &&
          typeof column.colId === "string",
      )
    ) {
      return columnState;
    }
  } catch {
    // Invalid saved state should fall back to the grid defaults.
  }

  localStorage?.removeItem(storageKey);
  return undefined;
};

const getRowSortValue = (row: GridRowInput, colDef: ColDef): unknown => {
  if (typeof colDef.valueGetter === "function") {
    return colDef.valueGetter({ data: row } as never);
  }
  if (colDef.field) {
    return (row as unknown as Record<string, unknown>)[colDef.field];
  }
  return undefined;
};

const compareSortValues = (
  valueA: unknown,
  valueB: unknown,
  colDef: ColDef,
): number => {
  if (colDef.comparator) {
    return colDef.comparator(
      valueA,
      valueB,
      null as never,
      null as never,
      false,
    );
  }
  if (valueA == null && valueB == null) return 0;
  if (valueA == null) return -1;
  if (valueB == null) return 1;
  if (typeof valueA === "number" && typeof valueB === "number") {
    return valueA - valueB;
  }
  return String(valueA).localeCompare(String(valueB), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

export const getSortedRows = (
  rows: GridRowInput[] | undefined,
  columnDefs: ColDef[],
  sortModel: GridSort[],
): GridRowInput[] | undefined => {
  if (!rows || !sortModel.length) return rows;

  const columnsById = new Map(
    columnDefs.map((colDef) => [getColumnId(colDef), colDef]),
  );

  return rows
    .map((row, index) => ({ row, index }))
    .sort((first, second) => {
      for (const sort of sortModel) {
        const colDef = columnsById.get(sort.colId);
        if (!colDef) continue;

        const direction = sort.sort === "desc" ? -1 : 1;
        const comparison =
          compareSortValues(
            getRowSortValue(first.row, colDef),
            getRowSortValue(second.row, colDef),
            colDef,
          ) * direction;
        if (comparison !== 0) return comparison;
      }
      return first.index - second.index;
    })
    .map(({ row }) => row);
};

const MyGrid: FC<{
  rows?: GridRowInput[];
  tabType: TabName;
  subTabType: SubTabName;
  oppositeRows: string;
  setPatientReleaseModal: React.Dispatch<
    React.SetStateAction<ReleasePatientData | undefined>
  >;
  setAddOnModal: React.Dispatch<
    React.SetStateAction<AddOnModalData | undefined>
  >;
  setNewReqOpen: React.Dispatch<React.SetStateAction<boolean>>;
  releaseToPatient: boolean;
  setDraftModal: React.Dispatch<
    React.SetStateAction<"saved" | "deleted" | "delete" | "unsaved" | undefined>
  >;
  onArchive?: (sfid: string) => void;
  onUnarchive?: (sfid: string) => void;
  onPrintError?: (message: string) => void;
  onOrderCfc: (row: RequisitionRow) => void;
  totalReqs?: number;
  suppressCfcBanners?: boolean;
  onEditOrder?: (order: NewRequisitionType) => void;
  editOrderEnabled?: boolean;
  onRequestReportUpdate?: (row: RequisitionRow) => void;
}> = ({
  rows,
  tabType,
  subTabType,
  setNewReqOpen,
  oppositeRows,
  setPatientReleaseModal,
  setAddOnModal,
  releaseToPatient,
  setDraftModal,
  onArchive,
  onUnarchive,
  onPrintError,
  onOrderCfc,
  totalReqs,
  suppressCfcBanners = false,
  onEditOrder,
  editOrderEnabled = false,
  onRequestReportUpdate,
}) => {
  const { physicalDeviceType } = useDeviceHook();
  // Capture persisted state before AG Grid emits its initial sort event and
  // writes the default state back to localStorage.
  const [savedColumnState] = useState(() => getSavedColumnState(tabType));
  const previousSortContext = useRef({
    physicalDeviceType,
    subTabType,
    tabType,
  });
  const [gridApi, setGridApi] = useState<GridApi>();
  const [currentPage, setCurrentPage] = useState(0);
  const [pendingSavedPage, setPendingSavedPage] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState(DEFAULT_GRID_PAGE_SIZE);
  const [sortModel, setSortModel] = useState<GridSort[] | null>(() =>
    savedColumnState ? getSortModelFromColumnState(savedColumnState) : null,
  );
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [selectedOrderData, setSelectedOrderData] =
    useState<NewRequisitionType | null>(null);
  const [phlebotomySnackbar, setPhlebotomySnackbar] = useState<{
    open: boolean;
    patientName: string;
  }>({ open: false, patientName: "" });
  const history = useHistory();
  const { auth } = useAuthenticationHook();
  const { enabled: smallAddOnButton } = useBooleanFeatureFlag(
    FeatureFlags.SmallAddOnButton,
  );
  const shouldShowCfcBanners =
    !suppressCfcBanners &&
    (tabType === "COMPLETED" || tabType === "PROCESSING");
  const closeOrderDetails = () => {
    setOrderDetailsOpen(false);
    setSelectedOrderData(null);
  };

  const defaultColDef = useMemo(
    () =>
      ({
        flex: 1,
        minWidth: 100,
        filter: false,
        filterParams: {
          newRowsAction: "keep",
        },
        sortable: false,
        resizable: true,
        cellClass: "no-border",
        suppressMovable: true,
      }) as ColDef,
    [],
  );

  const rowClassRules = useMemo(
    () => ({
      "row-not-read": ({ data }: { data?: GridRowData }) =>
        isProviderGridRow(data) && data.unread,
      "row-missing-info": ({ data }: { data?: GridRowData }) =>
        isProviderGridRow(data) && data.missingInfo,
      "row-electronic-order": ({ data }: { data?: GridRowData }) =>
        tabType === "ORDERED" && !isCfcBannerRow(data),
      "row-processing-completed": ({ data }: { data?: GridRowData }) =>
        tabType !== "ORDERED" && !isCfcBannerRow(data),
      "cfc-banner-row": ({ data }: { data?: GridRowData }) =>
        isCfcBannerRow(data),
      "row-cfc-frame": ({ data }: { data?: GridRowData }) =>
        shouldShowCfcBanners &&
        isProviderGridRow(data) &&
        isEligibleForCfc(data, { requireConditions: true }),
    }),
    [tabType, shouldShowCfcBanners],
  );

  const getRowHeight = useCallback(
    (params: { data?: GridRowData }) =>
      isCfcBannerRow(params.data)
        ? CFC_BANNER_HEIGHT
        : PROVIDER_GRID_ROW_HEIGHT,
    [],
  );

  const columnDefs = useMemo(
    () =>
      getColDefs(
        tabType,
        physicalDeviceType,
        subTabType,
        setNewReqOpen,
        setPatientReleaseModal,
        setAddOnModal,
        setDraftModal,
        releaseToPatient,
        auth?.user_type === "sales_rep",
        setPhlebotomySnackbar,
        onArchive,
        onUnarchive,
        onPrintError,
        smallAddOnButton,
        onEditOrder,
        editOrderEnabled,
        onRequestReportUpdate,
      ),
    [
      auth?.user_type,
      onArchive,
      onPrintError,
      onEditOrder,
      onRequestReportUpdate,
      onUnarchive,
      physicalDeviceType,
      releaseToPatient,
      setDraftModal,
      setNewReqOpen,
      setPatientReleaseModal,
      setAddOnModal,
      editOrderEnabled,
      smallAddOnButton,
      subTabType,
      tabType,
    ],
  );

  const defaultSortModel = useMemo(
    () => getDefaultSortModel(columnDefs),
    [columnDefs],
  );
  const activeSortModel = sortModel ?? defaultSortModel;
  const sortedRows = useMemo(
    () => getSortedRows(rows, columnDefs, activeSortModel),
    [activeSortModel, columnDefs, rows],
  );

  const rowCount = rows?.length ?? 0;
  const pageCount = Math.max(Math.ceil(rowCount / pageSize), 1);
  const lastPage = pageCount - 1;
  const effectiveCurrentPage = Math.min(currentPage, lastPage);
  const pageStartIndex = rowCount ? effectiveCurrentPage * pageSize : 0;
  const pageEndIndex = Math.min(rowCount, pageStartIndex + pageSize);

  const paginatedRows = useMemo(
    () => getPaginatedRows(sortedRows, effectiveCurrentPage, pageSize),
    [sortedRows, effectiveCurrentPage, pageSize],
  );

  const displayRows = useMemo<GridRowData[] | undefined>(
    () => buildDisplayRowsWithCfcBanners(paginatedRows, shouldShowCfcBanners),
    [paginatedRows, shouldShowCfcBanners],
  );

  const cfcBannerContext = useMemo<CfcBannerContext>(
    () => ({ onOrderCfc, currentPage: effectiveCurrentPage }),
    [effectiveCurrentPage, onOrderCfc],
  );

  useEffect(() => {
    const previous = previousSortContext.current;
    if (
      previous.physicalDeviceType === physicalDeviceType &&
      previous.subTabType === subTabType &&
      previous.tabType === tabType
    ) {
      return;
    }
    previousSortContext.current = {
      physicalDeviceType,
      subTabType,
      tabType,
    };

    setSortModel(defaultSortModel);
    if (gridApi && !gridApi.isDestroyed()) {
      gridApi.applyColumnState({
        defaultState: { sort: null },
        state: defaultSortModel,
      });
    }
  }, [defaultSortModel, gridApi, physicalDeviceType, subTabType, tabType]);

  useEffect(() => {
    const savedPage = localStorage?.getItem(
      `${SAVED_PAGE_KEY_PREFIX}${tabType}`,
    );
    if (!savedPage) {
      setPendingSavedPage(null);
      return;
    }

    try {
      const parsedPage = JSON.parse(savedPage);
      setPendingSavedPage(Number.isInteger(parsedPage) ? parsedPage : null);
    } catch {
      setPendingSavedPage(null);
      localStorage?.removeItem(`${SAVED_PAGE_KEY_PREFIX}${tabType}`);
    }
  }, [tabType]);

  useEffect(() => {
    if (pendingSavedPage === null || rows === undefined) return;

    setCurrentPage(Math.max(0, Math.min(pendingSavedPage, lastPage)));
    setPendingSavedPage(null);
    localStorage?.removeItem(`${SAVED_PAGE_KEY_PREFIX}${tabType}`);
  }, [lastPage, pendingSavedPage, rows, tabType]);

  useEffect(() => {
    if (!gridApi) return undefined;

    const timeoutId = setTimeout(() => {
      if (!gridApi.isDestroyed()) {
        gridApi.sizeColumnsToFit();
        if (tabType === "ORDERED") {
          gridApi.autoSizeColumns(["name"]);
        }
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [gridApi, tabType]);

  const handleCellClicked = useCallback(
    (event: CellClickedEvent<GridRowData>) => {
      const clickedData = event.data;
      if (!clickedData || isCfcBannerRow(clickedData)) return;
      if (physicalDeviceType !== "desktop") return;
      if (event.event?.defaultPrevented) return;

      if (tabType === "ORDERED") {
        if (hasSelectedTextInClickedCell(event.event)) return;
        if (hasSuppressedGridClickTarget(event.event)) return;
        if (isOrderedGridRow(clickedData)) {
          setSelectedOrderData(clickedData);
          setOrderDetailsOpen(true);
        }
        return;
      }

      if (!shouldOpenRequisitionFromColumn(event.column.getColId())) return;

      localStorage?.setItem(
        `${SAVED_PAGE_KEY_PREFIX}${tabType}`,
        JSON.stringify(effectiveCurrentPage),
      );
      if (isProviderGridRow(clickedData) && clickedData.sfID) {
        history.push(`${REQUISITION_URL}/${clickedData.sfID}`);
      }
    },
    [effectiveCurrentPage, history, physicalDeviceType, tabType],
  );

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setCurrentPage(0);
  };

  return (
    <GridWithPagination>
      <GridBody>
        <AGGridWrapper className={getGridClassName(physicalDeviceType)}>
          <AgGridReact<GridRowData>
            defaultColDef={defaultColDef}
            pagination={false}
            rowClassRules={rowClassRules}
            columnDefs={columnDefs}
            rowData={displayRows}
            context={cfcBannerContext}
            isFullWidthRow={({ rowNode }) => isCfcBannerRow(rowNode.data)}
            fullWidthCellRenderer={CfcBannerFullWidthRenderer}
            noRowsOverlayComponent={NoRowsRenderer}
            loadingOverlayComponent={LoadingRowsRenderer}
            noRowsOverlayComponentParams={{
              oppositeRows,
              tabType,
              subTabType,
            }}
            onGridReady={(e) => {
              if (savedColumnState) {
                e.api.applyColumnState({ state: savedColumnState });
              }
              setSortModel(getSortModelFromColumnState(e.api.getColumnState()));
              setGridApi(e.api);
            }}
            onSortChanged={(e) => {
              const currentState = e.api.getColumnState();
              setSortModel(getSortModelFromColumnState(currentState));
              localStorage?.setItem(
                `btoProviderColumnState${tabType}`,
                JSON.stringify(currentState),
              );
            }}
            getRowId={(row) => getGridRowId(row.data)}
            getRowHeight={getRowHeight}
            onCellClicked={handleCellClicked}
          />
        </AGGridWrapper>
      </GridBody>
      <GridPagination
        currentPage={effectiveCurrentPage}
        pageCount={pageCount}
        pageEndIndex={pageEndIndex}
        pageSize={pageSize}
        pageStartIndex={pageStartIndex}
        rowCount={rowCount}
        totalReqs={totalReqs}
        onPageChange={setCurrentPage}
        onPageSizeChange={handlePageSizeChange}
      />
      {selectedOrderData &&
        (isCfcOrder(selectedOrderData) ? (
          <CfcOrderDetailsDialog
            open={orderDetailsOpen}
            onClose={closeOrderDetails}
            orderData={selectedOrderData}
          />
        ) : (
          <NewReqOrderDialog
            open={orderDetailsOpen}
            onClose={closeOrderDetails}
            orderData={selectedOrderData}
          />
        ))}

      <StyledPhlebotomySnackbar
        open={phlebotomySnackbar.open}
        autoHideDuration={4000}
        onClose={() => setPhlebotomySnackbar({ open: false, patientName: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() =>
            setPhlebotomySnackbar({ open: false, patientName: "" })
          }
          severity="success"
          variant="filled"
        >
          {`Phlebotomy successfully requested for ${phlebotomySnackbar.patientName}`}
        </Alert>
      </StyledPhlebotomySnackbar>
    </GridWithPagination>
  );
};

export default MyGrid;
